import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { PopoverAnchor } from '@rocco/components/popover';
import { usePlaceFinder } from '@modules/location/hook/use-place-finder';
import { PostcodePopover } from '~/modules/enquiry/component/PostcodePopover';
import {
    GateFieldError,
    GateFieldLabel,
    GateInput,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';

type PlaceOption = ReturnType<typeof usePlaceFinder>['dropdownOptions'][number];

/** The form value the location question holds: a label plus the picked place. */
export interface GateLocationValue {
    header: string;
    value: { addressID?: string };
}

export interface EnquiryGateLocationFieldProps {
    label: string;
    value: GateLocationValue | undefined;
    error?: string;
    disabled?: boolean;
    /** Called with the picked place, or `undefined` when the field is cleared. */
    onSelect: (place: PlaceOption | undefined) => void;
    onBlur?: () => void;
}

/**
 * The location question in the gate's field design: type a suburb or
 * postcode, pick from the place finder's suggestions. Leaving the field with
 * text but no pick takes the first suggestion, so a typed answer is never lost.
 */
export const EnquiryGateLocationField = ({
    label,
    value,
    error,
    disabled,
    onSelect,
    onBlur,
}: EnquiryGateLocationFieldProps) => {
    const [search, setSearch] = useState(value?.header ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const {
        changeKeyword: findPlace,
        dropdownOptions: placeOptions,
        loading: isLoading,
    } = usePlaceFinder({ limit: 10, debounceMs: 250 });

    // A restored draft sets the value after mount; show it once it lands.
    useEffect(() => {
        if (value?.header && document.activeElement !== inputRef.current) {
            setSearch(value.header);
        }
    }, [value?.header]);

    useEffect(() => {
        setHighlightedIndex(placeOptions.length > 0 ? 0 : -1);
    }, [placeOptions]);

    const select = useCallback(
        (place: PlaceOption | undefined) => {
            setSearch(place?.header ?? '');
            setIsOpen(false);
            onSelect(place);
        },
        [onSelect],
    );

    // Waits for the suggestions to answer the current text: while a search is
    // loading, the list still holds the previous text's places.
    const selectFirstIfUnpicked = useCallback(() => {
        if (search && placeOptions.length && !value && !isLoading) {
            select(placeOptions[0]);
        }
    }, [search, placeOptions, value, isLoading, select]);

    // Suggestions that land after the reader has left the field still fill it.
    useEffect(() => {
        if (document.activeElement !== inputRef.current) {
            selectFirstIfUnpicked();
        }
    }, [selectFirstIfUnpicked]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) return;
        // The list still holds the previous text's places while a search
        // loads: Enter waits for this text's results instead of picking one
        // of those (or submitting the form with no place).
        if (isLoading) {
            if (event.key === 'Enter') event.preventDefault();
            return;
        }
        if (!placeOptions.length) return;
        const last = placeOptions.length - 1;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setHighlightedIndex((prev) => (prev < last ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : last));
                break;
            case 'Enter':
                event.preventDefault();
                select(placeOptions[Math.max(highlightedIndex, 0)]);
                break;
            case 'Escape':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <GateFieldLabel>{label}</GateFieldLabel>
            <PostcodePopover
                open={isOpen}
                usePortal
                surface="white"
                // The anchor is a zero-width point, so the panel sizes itself:
                // wide enough for "1041, Auckland, New Zealand" without running
                // past the card.
                contentClassName="w-[17rem]"
                onOpenChange={(open) => {
                    if (!open && document.activeElement === inputRef.current) {
                        return;
                    }
                    setIsOpen(open);
                }}
                value={value}
                placeOptions={placeOptions}
                isLoading={isLoading}
                highlightedIndex={highlightedIndex}
                onSelect={select}
            >
                <div className="relative">
                    <GateInput
                        ref={inputRef}
                        className={cn(
                            'pr-9',
                            error && 'ring-1 ring-destructive',
                        )}
                        placeholder={label}
                        aria-label={label}
                        aria-invalid={!!error}
                        // Its own suggestions sit under this field, so the
                        // browser's autofill list must not cover them.
                        autoComplete="off"
                        maxLength={100}
                        disabled={disabled}
                        value={search}
                        onChange={(event) => {
                            const next = event.target.value;
                            setSearch(next);
                            setHighlightedIndex(-1);
                            findPlace(next);
                            if (!next) {
                                select(undefined);
                                return;
                            }
                            setIsOpen(true);
                            // Typing over a picked place un-picks it, so the
                            // answer is what the field now shows, not the
                            // place picked before.
                            if (value && next !== value.header) {
                                onSelect(undefined);
                            }
                        }}
                        onFocus={(event) => {
                            // A place already picked (or filled in for them) has nothing to
                            // suggest; focus alone, such as the modal focusing its first field,
                            // must not open a list. Typing still does.
                            if (search && !value) {
                                setIsOpen(true);
                                if (!placeOptions.length) findPlace(search);
                            }
                            event.target.select?.();
                        }}
                        onBlur={() => {
                            selectFirstIfUnpicked();
                            setIsOpen(false);
                            setHighlightedIndex(-1);
                            onBlur?.();
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    {search && (
                        <button
                            type="button"
                            tabIndex={-1}
                            className="absolute inset-y-0 right-2.5 flex items-center text-black/50 hover:text-black/80"
                            aria-label={`Clear ${label.toLowerCase()}`}
                            onPointerDown={(event) => event.preventDefault()}
                            onClick={() => select(undefined)}
                        >
                            <X className="size-4" />
                        </button>
                    )}
                    {/* The list opens from the input's bottom edge. */}
                    <PopoverAnchor className="absolute top-full left-0" />
                </div>
            </PostcodePopover>
            <GateFieldError message={error} />
        </div>
    );
};
